/**
 * TourOverlay — the narration card. Fixed bottom-right while a tour is
 * active. Shows the current chapter/step, 4-field narrative, progress, and
 * navigation controls. Includes a "Jump to chapter" menu and a viewing-mode
 * toggle (Full / Express / Role-filtered).
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, List, CheckCircle2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useAccountingTour } from "@/data/accounting/tour/TourContext";
import { TOUR_CHAPTERS, getStepByIndex, type TourViewMode } from "@/data/accounting/tour/tourChapters";

const MODE_LABELS: Record<string, string> = {
  full: "Full tour",
  express: "Express (~5 min)",
  "role:leadership": "Role · Leadership",
  "role:finance": "Role · Finance",
  "role:ops": "Role · Ops",
  "role:field": "Role · Field",
  "role:admin": "Role · Admin",
};

function modeKey(m: TourViewMode): string {
  return m.kind === "role" ? `role:${m.role}` : m.kind;
}

function modeFromKey(k: string): TourViewMode {
  if (k === "full") return { kind: "full" };
  if (k === "express") return { kind: "express" };
  const [, role] = k.split(":");
  return { kind: "role", role: role as TourViewMode extends { kind: "role"; role: infer R } ? R : never };
}

export function TourOverlay() {
  const tour = useAccountingTour();
  const [collapsed, setCollapsed] = useState(false);

  const resolved = useMemo(() => getStepByIndex(tour.currentIndex), [tour.currentIndex]);
  if (!tour.active || !resolved) return null;

  const { chapter, step, chapterIndex, stepIndexInChapter } = resolved;
  const isFirst = tour.positionInMode === 0;
  const isLast = tour.positionInMode >= tour.totalSteps - 1;
  const currentModeKey = modeKey(tour.mode);

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-[100] rounded-full bg-primary text-primary-foreground shadow-xl px-4 py-2 text-sm font-medium flex items-center gap-2 hover:opacity-90"
      >
        Tour · Step {tour.positionInMode + 1}/{tour.totalSteps}
        <ChevronRight className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[100] w-[min(440px,calc(100vw-2rem))] rounded-lg border border-border bg-card shadow-2xl ring-1 ring-primary/20 animate-in slide-in-from-bottom-4 duration-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30 rounded-t-lg flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] shrink-0">
              Chapter {chapter.number}/{TOUR_CHAPTERS.length}
            </Badge>
            <span className="text-xs text-muted-foreground truncate">{chapter.title}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Mode toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="View mode" title={MODE_LABELS[currentModeKey] ?? "View mode"}>
                <Eye className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Viewing mode</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup value={currentModeKey} onValueChange={(v) => tour.setMode(modeFromKey(v))}>
                <DropdownMenuRadioItem value="full">Full tour (all steps)</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="express">Express (~5 min)</DropdownMenuRadioItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Filter by role</DropdownMenuLabel>
                <DropdownMenuRadioItem value="role:leadership">Leadership</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="role:finance">Finance</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="role:ops">Operations</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="role:field">Field</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="role:admin">Admin / Architecture</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Chapter jump */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Jump to chapter">
                <List className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Jump to chapter</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {TOUR_CHAPTERS.map((c, i) => (
                <DropdownMenuItem key={c.id} onClick={() => tour.jumpToChapter(i)} className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-medium">{c.number}. {c.title}</span>
                  <span className="text-[11px] text-muted-foreground">{c.subtitle}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCollapsed(true)} aria-label="Minimize">
            <ChevronRight className="h-4 w-4 rotate-90" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={tour.exit} aria-label="Exit tour">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
        <div>
          <h3 className="text-base font-semibold tracking-tight leading-snug">{step.title}</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Step {stepIndexInChapter + 1} of {chapter.steps.length} in this chapter · {MODE_LABELS[currentModeKey] ?? "Custom"}
          </p>
        </div>

        <NarrativeBlock label="What it is" body={step.what} />
        <NarrativeBlock label="Why it matters" body={step.why} tone="primary" />
        <NarrativeBlock label="How it connects" body={step.connects} />
        <NarrativeBlock label="Without it" body={step.without} tone="warn" />
      </div>

      {/* Footer / controls */}
      <div className="px-4 py-3 border-t border-border bg-muted/20 rounded-b-lg flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: TOUR_CHAPTERS.length }).map((_, i) => (
            <button
              key={i}
              onClick={() => tour.jumpToChapter(i)}
              className={
                "h-1.5 rounded-full transition-all " +
                (i === chapterIndex ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/60")
              }
              aria-label={`Jump to chapter ${i + 1}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground tabular-nums">{tour.positionInMode + 1}/{tour.totalSteps}</span>
          <Button variant="ghost" size="sm" onClick={tour.prev} disabled={isFirst}>
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
          </Button>
          {isLast ? (
            <Button size="sm" onClick={() => { tour.complete(); }}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Finish
            </Button>
          ) : (
            <Button size="sm" onClick={tour.next}>
              Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function NarrativeBlock({ label, body, tone }: { label: string; body: string; tone?: "primary" | "warn" }) {
  const labelClass =
    tone === "primary" ? "text-primary"
    : tone === "warn" ? "text-amber-600 dark:text-amber-400"
    : "text-muted-foreground";
  return (
    <div>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${labelClass}`}>{label}</div>
      <p className="text-[13px] leading-relaxed text-foreground/90 mt-0.5">{body}</p>
    </div>
  );
}
